import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ChangeEvent } from 'react'
import type { RefFile } from './MessageList'
import { ComposerIconActions } from './ComposerIconActions'
import { BusinessSkillsPanel } from './skills/BusinessSkillsPanel'
import { useSkills } from '../hooks/useSkills'
import { useMicDictation } from '../hooks/useSpeechRecognition'

const DEFAULT_STARTERS = [
  {
    icon: '🧭',
    title: 'Clarity of business goals',
    desc: 'Define or refine your financial, functional, and learning goals',
  },
  {
    icon: '🌱',
    title: 'Strategy for business growth',
    desc: 'Explore customer focus, product funnels, and market expansion',
  },
  {
    icon: '📈',
    title: 'Marketing and sales thinking',
    desc: 'Generate ideas to improve visibility, leads, and revenue',
  },
  {
    icon: '💡',
    title: 'Business functions and systems',
    desc: 'Think through improvements in marketing, sales, operations, or management',
  },
]

const FEATURED_SKILL_COUNT = 3

interface WelcomeScreenProps {
  onSend: (text: string, files?: RefFile[], fileToUpload?: File | null) => void
  onInvokeSkill: (displayMessage: string, skillId: string) => void
  onOpenGuide: () => void
  userName?: string
}

const StarterCard = ({
  icon,
  title,
  desc,
  onClick,
}: {
  icon: string
  title: string
  desc: string
  onClick: () => void
}) => {
  const [hov, setHov] = useState(false)
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      style={{
        background: 'var(--bg)',
        border: `1px solid ${hov ? 'rgba(37,99,235,0.25)' : 'var(--bd)'}`,
        borderRadius: 12,
        padding: '13px 14px 14px',
        cursor: 'pointer',
        transition: 'all .22s cubic-bezier(.16,1,.3,1)',
        transform: hov ? 'translateY(-2px)' : 'none',
        boxShadow: hov ? '0 4px 18px rgba(27,42,74,0.09)' : 'none',
        textAlign: 'left',
      }}
    >
      <span style={{ fontSize: 17, lineHeight: 1, display: 'inline-block', marginBottom: 4, verticalAlign: 'middle', marginRight: 8 }}>{icon}</span>
      <div
        style={{
          fontSize: 12.5,
          fontWeight: 500,
          display: 'inline-block',
          color: hov ? 'var(--text)' : 'var(--muted2)',
          lineHeight: 1.2,
          marginBottom: 3,
          transition: 'color .18s',
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 11.5,
          color: 'rgba(148, 163, 184, 0.72)',
          lineHeight: 1.5,
        }}
      >
        {desc}
      </div>
    </div>
  )
}

const SkillChip = ({ label, onClick }: { label: string; onClick: () => void }) => {
  const [hov, setHov] = useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 5,
        flex: '1 1 0',
        minWidth: 0,
        height: 34,
        boxSizing: 'border-box',
        background: hov ? 'rgba(37,99,235,0.06)' : 'var(--white)',
        border: `1px solid ${hov ? 'rgba(37,99,235,0.35)' : 'rgba(0,0,0,0.10)'}`,
        borderRadius: 100,
        padding: '0 10px',
        fontSize: 12,
        fontWeight: 400,
        lineHeight: 1,
        color: hov ? 'var(--blue)' : 'var(--text)',
        cursor: 'pointer',
        transition: 'all .18s',
      }}
    >
      <span style={{ color: 'var(--gold)', fontSize: 11, flexShrink: 0, lineHeight: 1 }}>⚡</span>
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {label}
      </span>
    </button>
  )
}

const WelcomeScreen = ({
  onSend,
  onInvokeSkill,
  onOpenGuide,
  userName = 'there',
}: WelcomeScreenProps) => {
  const { skills } = useSkills()
  const [text, setText] = useState('')
  const [files, setFiles] = useState<RefFile[]>([])
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [panelOpen, setPanelOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const taRef = useRef<HTMLTextAreaElement>(null)
  const textRef = useRef('')

  textRef.current = text

  const featured = skills.slice(0, FEATURED_SKILL_COUNT)

  const resize = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 110) + 'px'
  }

  const applyDictation = useCallback((fullText: string) => {
    setText(fullText)
    requestAnimationFrame(() => {
      if (!taRef.current) return
      taRef.current.focus()
      resize(taRef.current)
    })
  }, [])

  const { isRecording, toggle: toggleMic, stop: stopMic } = useMicDictation({
    getBaseText: () => textRef.current,
    onText: applyDictation,
  })

  useEffect(() => {
    const sync = () => setIsMobile(window.innerWidth < 640)
    sync()
    window.addEventListener('resize', sync)
    return () => window.removeEventListener('resize', sync)
  }, [])

  const send = () => {
    const fullText = text.trim()
    if (!fullText && !files.length) return
    if (isRecording) stopMic()
    setPanelOpen(false)
    onSend(fullText, files, pendingFile)
    setText('')
    setFiles([])
    setPendingFile(null)
    if (taRef.current) taRef.current.style.height = 'auto'
  }

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  const addFile = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    const ext = f.name.split('.').pop()?.toLowerCase() ?? ''
    setFiles((prev) => [...prev, { name: f.name, ext }])
    setPendingFile(f)
    e.target.value = ''
  }

  const invokeSkill = (title: string, id: string) => {
    setPanelOpen(false)
    onInvokeSkill(title, id)
  }

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '28px 24px',
        position: 'relative',
        isolation: 'isolate',
      }}
    >
      {panelOpen && (
        <div
          onClick={() => setPanelOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,30,56,0.52)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            zIndex: 10,
            animation: 'bIn .3s ease both',
          }}
        />
      )}

      <div
        style={{
          width: '100%',
          maxWidth: 680,
          margin: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            animation: 'wIn .7s .15s cubic-bezier(.16,1,.3,1) both',
            position: 'relative',
            zIndex: 1,
          }}
        >
        <div
          style={{
            width: 54,
            height: 54,
            borderRadius: 16,
            background: 'var(--navy)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 28px rgba(27,42,74,.2)',
            marginBottom: 16,
          }}
        >
          <span
            style={{
              fontFamily: "'Playfair Display',serif",
              fontSize: 24,
              fontWeight: 500,
              color: 'var(--gold)',
            }}
          >
            A
          </span>
        </div>

        <h2
          style={{
            fontFamily: "'Playfair Display',serif",
            fontSize: 26,
            fontWeight: 400,
            color: 'var(--navy)',
            lineHeight: 1.3,
            textAlign: 'center',
            marginBottom: 9,
          }}
        >
          What&apos;s on your mind,{' '}
          <em style={{ color: 'var(--blue)', fontStyle: 'italic' }}>{userName}?</em>
        </h2>
        <p
          style={{
            fontSize: 13.5,
            color: 'var(--muted)',
            maxWidth: 400,
            lineHeight: 1.65,
            textAlign: 'center',
            marginBottom: 26,
          }}
        >
          Ask me anything about your business. Explore ideas, refine your goals, and work through
          strategies.
        </p>

        <div
          style={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 9,
            marginBottom: 18,
          }}
        >
          {DEFAULT_STARTERS.map((s) => (
            <StarterCard key={s.title} {...s} onClick={() => onSend(s.title)} />
          ))}
        </div>

        <div
          style={{
            width: '100%',
            background: 'var(--bg)',
            border: '1.5px solid var(--bd)',
            borderRadius: 14,
            padding: '12px 14px 9px',
          }}
        >
          {files.length > 0 && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
                marginBottom: 8,
                paddingBottom: 8,
                borderBottom: '1px solid var(--bd2)',
              }}
            >
              {files.map((f) => (
                <div
                  key={f.name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'var(--blue-lt)',
                    border: '1px solid var(--blue-bd)',
                    borderRadius: 6,
                    padding: '4px 10px',
                    fontSize: 11,
                    color: 'var(--blue)',
                  }}
                >
                  <span>{f.name}</span>
                  <span
                    onClick={() => {
                      setFiles((prev) => prev.filter((x) => x.name !== f.name))
                      if (pendingFile?.name === f.name) setPendingFile(null)
                    }}
                    style={{ cursor: 'pointer', color: 'var(--muted)' }}
                  >
                    ×
                  </span>
                </div>
              ))}
            </div>
          )}
          <textarea
            ref={taRef}
            value={text}
            onChange={(e) => {
              setText(e.target.value)
              resize(e.target)
            }}
            onKeyDown={onKey}
            placeholder={isRecording ? 'Listening… speak now' : 'Ask Athena anything about your business…'}
            rows={1}
            style={{
              width: '100%',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 13.5,
              color: '#1E293B',
              caretColor: '#1B2A4A',
              resize: 'none',
              lineHeight: 1.65,
              minHeight: 38,
              maxHeight: 110,
              overflowY: 'auto',
              display: 'block',
            }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginTop: 8,
              paddingTop: 8,
              borderTop: '1px solid rgba(0,0,0,0.05)',
            }}
          >
            <ComposerIconActions
              onFileChange={addFile}
              isRecording={isRecording}
              onMicClick={toggleMic}
            />
            <button
              type="button"
              onClick={send}
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                background: 'var(--navy)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--white)',
                fontSize: 16,
                boxShadow: '0 2px 8px rgba(27,42,74,.2)',
              }}
            >
              ↑
            </button>
          </div>
        </div>
        </div>

        <div
          style={{
            position: 'relative',
            width: '100%',
            marginTop: 14,
            paddingTop: 14,
            borderTop: '1px solid rgba(0,0,0,0.06)',
            display: 'flex',
            flexDirection: 'column',
            gap: 9,
          }}
        >
          {panelOpen && (
            <BusinessSkillsPanel
              skills={skills}
              isMobile={isMobile}
              onSelect={invokeSkill}
              onClose={() => setPanelOpen(false)}
              placement="popover"
            />
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 7 }}>
              <span
                style={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '1.6px',
                  textTransform: 'uppercase',
                  color: 'var(--navy)',
                }}
              >
                Business skills ({skills.length})
              </span>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--gold)',
                  background: 'rgba(201,168,76,0.14)',
                  border: '1px solid rgba(201,168,76,0.32)',
                  borderRadius: 4,
                  padding: '1px 6px',
                  lineHeight: 1.4,
                  whiteSpace: 'nowrap',
                }}
              >
                ⚡ Guided
              </span>
            </span>
          </div>

          <div className="no-sb" style={{ display: 'flex', width: '100%', gap: 8, alignItems: 'stretch' }}>
            {featured.map((skill) => (
              <SkillChip
                key={skill.id}
                label={skill.title}
                onClick={() => invokeSkill(skill.title, skill.id)}
              />
            ))}
            <button
              type="button"
              onClick={() => setPanelOpen((o) => !o)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '1 1 0',
                minWidth: 0,
                height: 34,
                boxSizing: 'border-box',
                background: 'var(--bg)',
                border: '1px dashed rgba(0,0,0,0.16)',
                borderRadius: 100,
                padding: '0 10px',
                fontSize: 12,
                fontWeight: 400,
                lineHeight: 1,
                color: 'var(--muted)',
                cursor: 'pointer',
              }}
            >
              {panelOpen ? 'Close' : 'View more'}
            </button>
          </div>

          <span style={{ fontSize: 11.5, color: 'var(--muted2)', lineHeight: 1.6 }}>
            Skills guide Athena into a focused format for your business — pick one to get started.
          </span>
        </div>

        <button
          type="button"
          onClick={onOpenGuide}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 18,
            fontSize: 12,
            color: 'var(--blue)',
            cursor: 'pointer',
            borderBottom: '1.5px dashed var(--blue-bd)',
            paddingBottom: 1,
            background: 'none',
            borderTop: 'none',
            borderLeft: 'none',
            borderRight: 'none',
            position: 'relative',
            zIndex: panelOpen ? 0 : 1,
          }}
        >
          ✦ &nbsp;New here? See how Athena works →
        </button>
      </div>
    </div>
  )
}

export default WelcomeScreen
